import { Component, OnInit, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { ConfirmDialogComponent } from '../../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { AdminAttributeDetail, AdminAttributeRow } from '../../../_interfaces/attribute';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';
import { AttributesService } from '../../../_services/attributes.service';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';

interface ValueRow {
  key: number; // stable identity for the list (new rows have no id yet)
  id: number; // 0 for a value that hasn't been saved
  value: string;
  slug: string;
  slugTouched: boolean;
  productCount: number;
}

// Create / edit an attribute (name, slug) together with its values. Values
// keep their ids across edits - products point at them - so the form only
// ever renames, adds or removes them; a value in use can't be removed.
@Component({
    selector: 'app-edit-attribute',
    imports: [MainLayoutComponent, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, RecordNavComponent],
    templateUrl: './edit-attribute.component.html'
})
export class EditAttributeComponent implements OnInit {
  private dialog = inject(MatDialog);

  isEditMode = false;
  attributeId: number | null = null;
  loading = false;
  saving = false;
  submitted = false;

  name = '';
  slug = '';
  private originalSlug = '';
  private slugTouched = false;
  values: ValueRow[] = [];
  private nextKey = 1;

  detail: AdminAttributeDetail | null = null;
  valueFilter = '';
  bulkText = '';
  bulkOpen = false;
  newValueText = '';

  private userInfo: any;

  // Previous / next: the attributes in the list's order.
  list: AdminAttributeRow[] = [];
  // What was loaded, to warn before leaving with unsaved changes.
  private loadedSnapshot = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private attributesService: AttributesService,
    private alertService: AlertService,
    private userService: UserService,
  ) {}

  ngOnInit(): void {
    this.userInfo = this.userService.isLoggedIn();
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode = true;
      this.attributeId = Number(idParam);
      this.load(this.attributeId);
      this.attributesService.getAdminAttributes().subscribe({
        next: (rows) => (this.list = rows),
        error: () => (this.list = []),
      });
    } else {
      this.bulkOpen = true;
    }
  }

  private load(id: number): void {
    this.loading = true;
    this.attributesService.getAdminAttribute(id).subscribe({
      next: (a) => {
        this.detail = a;
        this.name = a.attributeName;
        this.slug = a.attributeSlug;
        this.originalSlug = a.attributeSlug;
        this.slugTouched = true;
        this.values = a.values.map((v) => ({
          key: this.nextKey++,
          id: v.id,
          value: v.attributeValue,
          slug: v.attributeValueSlug,
          slugTouched: true,
          productCount: v.productCount,
        }));
        this.loading = false;
        this.loadedSnapshot = this.snapshot;
      },
      error: (error) => {
        this.loading = false;
        this.alertService.alert(error?.error?.message || 'Attribute not found', 'error');
        this.router.navigate(['/attributes']);
      },
    });
  }

  // ---- previous / next ----
  private get snapshot(): string {
    return JSON.stringify([this.name, this.slug, this.values.map((v) => [v.id, v.value, v.slug])]);
  }

  get dirty(): boolean {
    return this.isEditMode && !this.loading && this.snapshot !== this.loadedSnapshot;
  }

  get position(): number {
    return this.list.findIndex((r) => r.attributeId === this.attributeId);
  }

  get prev(): AdminAttributeRow | null {
    return this.position > 0 ? this.list[this.position - 1] : null;
  }

  get next(): AdminAttributeRow | null {
    const i = this.position;
    return i >= 0 && i < this.list.length - 1 ? this.list[i + 1] : null;
  }

  goTo(row: AdminAttributeRow | null): void {
    if (!row || this.saving) return;
    confirmLeave(this.dialog, this.dirty, this.name, row.attributeName).subscribe((ok) => {
      if (ok) this.router.navigate(['/attributes/edit', row.attributeId]);
    });
  }

  // ---- slugs (same rules as the server) ----
  private static slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9²]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  onNameInput(): void {
    if (!this.slugTouched && !this.isEditMode) {
      const s = EditAttributeComponent.slugify(this.name);
      this.slug = s ? 'pa_' + s : '';
    }
  }

  onSlugInput(): void {
    this.slugTouched = true;
  }

  regenerateSlug(): void {
    const s = EditAttributeComponent.slugify(this.name);
    this.slug = s ? 'pa_' + s : '';
    this.slugTouched = true;
  }

  // The product forms look attributes up by slug, so it's locked for the core
  // ones and for any attribute products already use.
  get slugLockedReason(): string {
    if (!this.isEditMode || !this.detail) return '';
    if (this.detail.core) return 'Used by the product forms, so the slug is fixed';
    if (this.detail.productCount > 0) return `${this.detail.productCount} product${this.detail.productCount === 1 ? ' uses' : 's use'} this attribute, so the slug can't change`;
    return '';
  }

  get slugChanged(): boolean {
    return this.isEditMode && !!this.originalSlug && this.slug !== this.originalSlug;
  }

  get nameError(): string {
    return this.submitted && !this.name.trim() ? 'Attribute name is required' : '';
  }

  get slugError(): string {
    if (!this.submitted) return '';
    if (this.slug.length > 100) return 'Slug is too long (100 characters at most)';
    return '';
  }

  // ---- values ----
  get shownValues(): ValueRow[] {
    const q = this.valueFilter.trim().toLowerCase();
    return q ? this.values.filter((v) => v.value.toLowerCase().includes(q) || v.slug.includes(q)) : this.values;
  }

  private nameCounts(): Map<string, number> {
    const map = new Map<string, number>();
    for (const v of this.values) {
      const k = v.value.trim().toLowerCase();
      if (k) map.set(k, (map.get(k) ?? 0) + 1);
    }
    return map;
  }

  isDuplicate(row: ValueRow): boolean {
    const k = row.value.trim().toLowerCase();
    return !!k && (this.nameCounts().get(k) ?? 0) > 1;
  }

  get duplicateCount(): number {
    let n = 0;
    for (const c of this.nameCounts().values()) if (c > 1) n++;
    return n;
  }

  get unusedCount(): number {
    return this.values.filter((v) => v.productCount === 0).length;
  }

  private makeRow(value: string): ValueRow {
    return { key: this.nextKey++, id: 0, value, slug: EditAttributeComponent.slugify(value), slugTouched: false, productCount: 0 };
  }

  onValueInput(row: ValueRow): void {
    // A saved value keeps its slug when renamed (it may be in links); a new
    // one follows what's typed until the slug is edited by hand.
    if (!row.slugTouched && row.id === 0) row.slug = EditAttributeComponent.slugify(row.value);
  }

  addValue(): void {
    const v = this.newValueText.trim();
    if (!v) return;
    if (this.values.some((x) => x.value.trim().toLowerCase() === v.toLowerCase())) {
      this.alertService.alert(`"${v}" is already in the list`, 'error');
      return;
    }
    this.values.push(this.makeRow(v));
    this.newValueText = '';
  }

  // Paste a list: one value per line, or comma separated.
  addBulk(): void {
    const items = this.bulkText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!items.length) return;
    const have = new Set(this.values.map((v) => v.value.trim().toLowerCase()));
    let added = 0;
    let skipped = 0;
    for (const item of items) {
      const k = item.toLowerCase();
      if (have.has(k)) {
        skipped++;
        continue;
      }
      have.add(k);
      this.values.push(this.makeRow(item));
      added++;
    }
    this.bulkText = '';
    this.alertService.alert(`${added} value${added === 1 ? '' : 's'} added${skipped ? `, ${skipped} skipped (already in the list)` : ''}`, added ? 'success' : 'error');
  }

  removeValue(row: ValueRow): void {
    if (row.productCount > 0) {
      this.alertService.alert(`"${row.value}" is used by ${row.productCount} product${row.productCount === 1 ? '' : 's'} and can't be removed`, 'error');
      return;
    }
    this.values = this.values.filter((v) => v.key !== row.key);
  }

  removeUnused(): void {
    const n = this.unusedCount;
    if (!n) return;
    this.values = this.values.filter((v) => v.productCount > 0);
    this.alertService.alert(`${n} unused value${n === 1 ? '' : 's'} removed - save to apply`, 'success');
  }

  // ---- actions ----
  get canDelete(): boolean {
    const roles = String(this.userInfo?.userrole ?? '').split(',').map((r) => r.trim());
    return ['ADMIN', 'SALEADMIN', 'S-ADMIN'].some((r) => roles.includes(r));
  }

  get deleteBlockedReason(): string {
    if (!this.detail) return '';
    if (this.detail.core) return 'Used by the product forms';
    if (this.detail.productCount > 0) return `${this.detail.productCount} product${this.detail.productCount === 1 ? ' uses' : 's use'} it`;
    return '';
  }

  onSave(): void {
    this.submitted = true;
    if (!this.name.trim()) {
      this.alertService.alert('Attribute name is required', 'error');
      return;
    }
    if (this.slugError) {
      this.alertService.alert(this.slugError, 'error');
      return;
    }
    if (this.duplicateCount) {
      this.alertService.alert('Some values are listed twice - highlighted in red', 'error');
      return;
    }
    const values = this.values.filter((v) => v.value.trim());

    this.saving = true;
    this.attributesService
      .saveAdminAttribute({
        attributeId: this.isEditMode ? (this.attributeId ?? undefined) : undefined,
        attributeName: this.name.trim(),
        attributeSlug: this.slug.trim(),
        values: values.map((v) => ({ id: v.id, attributeValue: v.value.trim(), attributeValueSlug: v.slug.trim() })),
      })
      .subscribe({
        next: (res) => {
          this.saving = false;
          this.alertService.alert(this.isEditMode ? 'Attribute updated' : 'Attribute created', 'success');
          this.router.navigate(['/attributes'], { queryParams: { selected: res.attributeId } });
        },
        error: (error) => {
          this.saving = false;
          this.alertService.alert(error?.error?.message || 'Could not save the attribute', 'error');
        },
      });
  }

  onDelete(): void {
    if (!this.detail) return;
    const d = this.detail;
    if (this.deleteBlockedReason) {
      this.alertService.alert(`"${d.attributeName}" can't be deleted: ${this.deleteBlockedReason}`, 'error');
      return;
    }
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete attribute',
        message: `Delete "${d.attributeName}"?`,
        detail: d.values.length ? `Its ${d.values.length} value${d.values.length === 1 ? '' : 's'} will be deleted too. No products use it.` : 'No products use it.',
        confirmText: 'Delete attribute',
        tone: 'danger',
      },
    });
    ref.afterClosed().subscribe((result) => {
      if (!result?.confirmation) return;
      this.attributesService.deleteAdminAttribute(d.attributeId).subscribe({
        next: () => {
          this.alertService.alert('Attribute deleted', 'success');
          this.router.navigate(['/attributes']);
        },
        error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete the attribute', 'error'),
      });
    });
  }

  onCancel(): void {
    this.router.navigate(['/attributes'], this.attributeId ? { queryParams: { selected: this.attributeId } } : {});
  }
}
